'use client';

import { useEffect, useRef } from 'react';
import { Skeleton } from '@skin-seoul/design-system';
import {
  CLASSNAME,
  CREDIT_REPURCHASE_LOOP_COPY,
  countReviewableCartProducts,
  formatLoyaltyCredit,
  useAnalyticsEvent,
  useCartService,
  useReviewService,
  useUser,
  type Currency,
} from '@skin-seoul/service';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import { LucideIcon } from '../../LucideIcon';

const CREDIT_PREVIEW_VIEW_EVENT = 'credit_preview_view';
const ANALYTICS_SURFACE = 'cart_drawer';
const ICON_SIZE_PX = 12;

type CartExpectedCreditRowProps = {
  currency: Currency;
};

const CartExpectedCreditContent = ({ currency }: CartExpectedCreditRowProps) => {
  const { useSuspenseExpectedCredit, useGetCartSuspenseQuery } = useCartService();
  const { useSuspenseReviewCreditAmounts } = useReviewService();
  const analytics = useAnalyticsEvent();

  const { data, isError } = useSuspenseExpectedCredit({ currency });
  const { data: cart } = useGetCartSuspenseQuery({ currency });
  const { data: reviewCreditAmounts } = useSuspenseReviewCreditAmounts({ currency });

  const { creditAmount: purchaseCredit, fractionDigits } = data;
  const reviewCredit = reviewCreditAmounts.mediaCreditAmount * countReviewableCartProducts(cart.cart.item);
  const creditAmount = purchaseCredit + reviewCredit;
  const isVisible = !isError && purchaseCredit > 0;

  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (!isVisible || hasTrackedViewRef.current) {
      return;
    }
    hasTrackedViewRef.current = true;
    analytics.trackCustom({
      eventName: CREDIT_PREVIEW_VIEW_EVENT,
      params: { surface: ANALYTICS_SURFACE, credit_amount: creditAmount, currency },
    });
  }, [analytics, creditAmount, currency, isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="flex w-full items-center gap-1 rounded-lg bg-primary-50 px-2 py-1">
      <LucideIcon name="Coins" size={ICON_SIZE_PX} className="text-primary-500 shrink-0 -scale-x-100" />
      <p className="text-label-small font-medium text-primary-500">
        {CREDIT_REPURCHASE_LOOP_COPY.orderEarnPrefix}
        <span className={`font-semibold ${CLASSNAME.NOT_TRANSLATE}`}>
          {formatLoyaltyCredit(currency, creditAmount, fractionDigits)}
        </span>
        {CREDIT_REPURCHASE_LOOP_COPY.orderEarnSuffix}
      </p>
    </div>
  );
};

export const CartExpectedCreditRow = ({ currency }: CartExpectedCreditRowProps) => {
  const { isAuthenticated } = useUser();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <QueryBoundary clientOnly loadingFallback={<Skeleton className="h-6 w-full rounded-lg" />} errorFallback={<></>}>
      <CartExpectedCreditContent currency={currency} />
    </QueryBoundary>
  );
};
